// src/hooks/usePrivateDealAssignment.js
// Shared private-deal awareness for the assign modals (risks + assessments).
//
// A deal with isPrivate=true only admits deal members as risk/assessment assignees —
// the backend 400s otherwise. This hook fetches GET /deals/{dealId}/members (readable
// by any user who can view the deal) when a modal opens in a deal context and exposes:
//   - isPrivateDeal        – true when the linked deal is private
//   - memberIds            – Set of member userIds (null until loaded / on failure)
//   - canManagePrivateDeal – current user holds OWNER or ADMIN at DEAL scope for this
//                            deal (may assign non-members after granting them access,
//                            and may assign by invite)
//   - isMember(userId)     – membership check (true while members are unknown)
//   - grantDealContributor – grant CONTRIBUTOR@DEAL to a user via the role-resubmit
//                            flow (same pattern as DealShareModal)
//
// Callers pass `isPrivate` when they already know it (deal DTOs carry it) so the
// members fetch is skipped for known-public deals; when unknown, the fetch itself
// reports isPrivate. Non-deal contexts (no dealId) are inert — zero behavior change.

import { useState, useEffect } from "react";
import { useApi } from "./useApi";
import { useRBAC } from "./useRBAC";
import { fetchUserRoles, updateUserRoles } from "../services/userRolesService";

// Role/scope values mirror the backend enums (see DealShareModal)
const DEAL_SCOPE = 5;
const CONTRIBUTOR_ROLE = 4;

export const usePrivateDealAssignment = (active, dealId, isPrivateHint) => {
    const apiCall = useApi();
    const { isDealOwner, isDealAdmin } = useRBAC();

    const [isPrivateDeal, setIsPrivateDeal] = useState(false);
    const [memberIds, setMemberIds] = useState(null);
    const [loadingMembers, setLoadingMembers] = useState(false);

    const parsedDealId = dealId ? parseInt(dealId) : null;
    // Skip the fetch when the caller already knows the deal is public
    const shouldCheck = !!(active && parsedDealId && isPrivateHint !== false);

    useEffect(() => {
        if (!shouldCheck) {
            setIsPrivateDeal(false);
            setMemberIds(null);
            return;
        }
        let cancelled = false;
        setLoadingMembers(true);
        apiCall("GET", `/deals/${parsedDealId}/members`)
            .then(res => {
                if (cancelled) return;
                setIsPrivateDeal(!!res?.isPrivate);
                setMemberIds(new Set((res?.members || []).map(m => m.userId)));
            })
            .catch(() => {
                // Fail open in the UI — the backend still rejects invalid assignees
                if (cancelled) return;
                setIsPrivateDeal(false);
                setMemberIds(null);
            })
            .finally(() => {
                if (!cancelled) setLoadingMembers(false);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldCheck, parsedDealId]);

    const canManagePrivateDeal =
        isPrivateDeal && (isDealOwner(parsedDealId) || isDealAdmin(parsedDealId));

    const isMember = (userId) => !memberIds || memberIds.has(parseInt(userId));

    // Rebuild the target user's role list with CONTRIBUTOR@DEAL added and resubmit via
    // POST /users/{id}/roles — the same replace-all-manageable-roles flow DealShareModal
    // uses. Roles outside the caller's manageable scope are preserved server-side.
    //
    // This grants membership only — it must NEVER downgrade an existing role on the deal.
    // Lower role values are stronger (OWNER=1 … VIEWER=6), so a user who already holds
    // OWNER/ADMIN/RISK_MANAGER on this deal is left untouched: silently replacing an
    // OWNER@DEAL with CONTRIBUTOR strips the only account able to manage a private deal's
    // access (org OWNER/ADMIN/RM have no path in), orphaning it. Mirrors the backend rule in
    // InviteAssessmentTransferService.GrantPrivateDealMembershipsAsync ("unless the user
    // already holds a role on it").
    const grantDealContributor = async (userId) => {
        const existing = (await fetchUserRoles(apiCall, userId)) || [];
        const existingDealRole = existing.find(
            r => r.scopeValue === DEAL_SCOPE && r.scopeEntityId === parsedDealId
        );
        if (existingDealRole && existingDealRole.roleValue <= CONTRIBUTOR_ROLE)
            return; // already a member at CONTRIBUTOR strength or stronger — nothing to do

        const kept = existing
            .filter(r => !(r.scopeValue === DEAL_SCOPE && r.scopeEntityId === parsedDealId))
            .map(r => ({ role: r.roleValue, scope: r.scopeValue, scopeEntityId: r.scopeEntityId }));
        await updateUserRoles(apiCall, userId, [
            ...kept,
            { role: CONTRIBUTOR_ROLE, scope: DEAL_SCOPE, scopeEntityId: parsedDealId },
        ]);
    };

    return {
        isPrivateDeal,
        memberIds,
        loadingMembers,
        canManagePrivateDeal,
        isMember,
        grantDealContributor,
    };
};

export default usePrivateDealAssignment;
